import { useDynamicLocation } from "apps/commons/hooks/useDynamicLocation";
import { withoutLicenseDocumentValidation } from "apps/oneOnboarding/contract/documentUpload/licenseDocumentValidation";
import { rateMapper } from "apps/oneOnboarding/pricing/constants";
import type { FormEventHandler, FunctionComponent, PropsWithChildren } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { BulkPropertyFooter } from "./BulkPropertyFooter";
import type { BulkPropertyFormData } from "./BulkPropertyFormData";
import { useBulkOnboardingFormHandler } from "./useBulkOnboardingFormHandler";

interface Props extends PropsWithChildren {
    data: Omit<BulkPropertyFormData, "contentPolicyAgreed">;
}

export const BulkPropertyForm: FunctionComponent<Props> = ({ data, children }) => {
    const path = useDynamicLocation();

    const isProfilePage = path.pathname === "/:lang/list-my-property/onboard/batch/profile";

    const { mutate, isLoading } = useBulkOnboardingFormHandler();

    const form = useForm<BulkPropertyFormData>({
        values: {
            ...data,
            contentPolicyAgreed: false,
        },
        shouldFocusError: false,
    });

    const handleSubmit = (data: BulkPropertyFormData) => {
        if (isProfilePage) return;
        return mutate({
            ...withoutLicenseDocumentValidation(data),
            propertyDataList:
                data.propertyDataList?.map((property) =>
                    property.rate === rateMapper.ChannelManager
                        ? {
                              ...property,
                              firstAllotment: null,
                              firstAllotmentDate: null,
                          }
                        : property,
                ) ?? null,
        });
    };

    const formSubmitEvent: FormEventHandler<HTMLFormElement> = (e) => {
        e.preventDefault();

        const formSubmitHandler = form.handleSubmit(handleSubmit);
        return formSubmitHandler();
    };

    return (
        <FormProvider {...form}>
            <form id="bulk-property-data-form" onSubmit={formSubmitEvent}>
                {children}
                <BulkPropertyFooter loading={isLoading} />
            </form>
        </FormProvider>
    );
};
